import { useState, useRef, useEffect } from "react";

import { motion } from "framer-motion";
import { toast } from "react-toastify";

import ResellerManagedBadge from "../components/ResellerManagedBadge";
import { useAuth } from "../contexts/AuthContext";
import useRouteTab from "../hooks/useRouteTab";
import apiService from "../services/api";
import { getCustomerPortal } from "../utils/stripe";

const PROFILE_TAB_IDS = ["general", "security", "preferences", "activity"];

const Profile = () => {
  const { user, updateProfile, updateAvatar, changePassword } = useAuth();
  const [activeTab, setActiveTab] = useRouteTab({
    defaultTab: "general",
    validTabs: PROFILE_TAB_IDS,
  });
  const [loading, setLoading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(user?.avatarUrl || null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setAvatarPreview(user?.avatarUrl || null);
  }, [user?.avatarUrl]);

  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    phone: user?.phone || "",
    bio: user?.bio || "",
    company: user?.company || "",
    position: user?.position || "",
    website: user?.website || "",
    location: user?.location || "",
    timezone: user?.timezone || "UTC",
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [preferences, setPreferences] = useState({
    showWelcomeBanner: user?.preferences?.showWelcomeBanner !== false, // Default to true
  });

  // Update local state when user changes
  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phone: user.phone || "",
        bio: user.bio || "",
        company: user.company || "",
        position: user.position || "",
        website: user.website || "",
        location: user.location || "",
        timezone: user.timezone || "UTC",
      });
      setAvatarPreview(user.avatarUrl || null);
      // Update preferences when user data changes
      if (user.preferences) {
        setPreferences({
          showWelcomeBanner: user.preferences.showWelcomeBanner !== false,
        });
      }
    }
  }, [user]);

  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];

    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Avatar size must be less than 5MB");

        return;
      }

      setLoading(true);

      try {
        const response = await apiService.uploadAvatar(file);
        const avatarUrl = response.data?.avatarUrl;

        // Update the user's profile with the new avatar URL
        // Avatar preview will be updated automatically via useEffect when user.avatarUrl changes
        await updateAvatar(avatarUrl);

        toast.success("Avatar updated successfully!");
      } catch (error) {
        toast.error(error.message || "Failed to upload avatar");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRemoveAvatar = async () => {
    setLoading(true);
    try {
      const result = await updateAvatar(null);

      if (result.success) {
        // Avatar preview will be updated automatically via useEffect when user.avatarUrl changes
        toast.success("Avatar removed successfully!");
      } else {
        toast.error(result.error || "Failed to remove avatar");
      }
    } catch (error) {
      toast.error("Failed to remove avatar");
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await updateProfile(profileData);

    if (result?.success) {
      toast.success("Profile updated successfully!");
    } else {
      toast.error(result?.error || "Failed to update profile");
    }

    setLoading(false);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("Passwords do not match");

      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");

      return;
    }

    setLoading(true);

    const result = await changePassword(
      passwordData.currentPassword,
      passwordData.newPassword
    );

    if (result?.success) {
      toast.success("Password changed successfully!");
      setPasswordData({
        newPassword: "",
        confirmPassword: "",
        currentPassword: "",
      });
    } else {
      toast.error(result?.error || "Failed to change password");
    }

    setLoading(false);
  };

  const handlePreferencesUpdate = async (newPreferences) => {
    try {
      // Use the AuthContext's updateProfile to update both API and local state
      const result = await updateProfile({
        preferences: newPreferences,
      });

      if (result?.success) {
        toast.success("Preferences updated successfully");
      } else {
        toast.error("Failed to update preferences");
      }
    } catch (error) {
      toast.error("Failed to update preferences");
      console.error("Error updating preferences:", error);
    }
  };

  const tabs = [
    { id: "general", label: "General", icon: "fas fa-user" },
    { id: "security", label: "Security", icon: "fas fa-shield-alt" },
    // Only show Preferences tab for premium users (not Free User or Trial)
    ...(user?.type === "Premium" || user?.type === "Reseller"
      ? [{ id: "preferences", label: "Preferences", icon: "fas fa-cog" }]
      : []),
    // { id: "activity", label: "Activity", icon: "fas fa-history" }, // Hidden for now - not ready yet
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Profile Sidebar */}
        <motion.div
          animate={{ opacity: 1, x: 0 }}
          className="lg:col-span-1"
          initial={{ opacity: 0, x: -20 }}
        >
          <div className="card p-6 shadow-md">
            <div className="text-center">
              <div className="relative inline-block">
                {avatarPreview ? (
                  <img
                    alt="Profile"
                    className="w-32 h-32 rounded-full object-cover mx-auto border-4 border-white shadow-lg"
                    src={avatarPreview}
                  />
                ) : (
                  <div className="w-32 h-32 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center text-white text-4xl font-bold mx-auto">
                    {user?.name
                      ?.split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase()}
                  </div>
                )}
                <input
                  accept="image/*"
                  className="hidden"
                  ref={fileInputRef}
                  type="file"
                  onChange={handleAvatarChange}
                />
                <button
                  className="absolute bottom-0 right-0 w-10 h-10 bg-white dark:bg-gray-700 rounded-full border-2 border-gray-200 dark:border-gray-600 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  title="Change avatar"
                  onClick={handleAvatarClick}
                >
                  <i className="fas fa-camera text-gray-600 dark:text-gray-400" />
                </button>
              </div>
              {avatarPreview && (
                <button
                  className="block mx-auto mt-3 text-sm text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                  onClick={handleRemoveAvatar}
                >
                  <i className="fas fa-trash mr-1" />
                  Remove Avatar
                </button>
              )}
              <h2 className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">
                {profileData.name || user?.name}
              </h2>
              <p className="text-gray-500">
                {profileData.email || user?.email}
              </p>
              <div className="flex flex-wrap gap-2 justify-center mt-2">
                {user?.role === "admin" ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400">
                    <i className="fas fa-shield-alt mr-1" />
                    Admin
                  </span>
                ) : (
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      user?.type === "Reseller"
                        ? "bg-[#e97b35] text-white"
                        : user?.type === "Premium"
                          ? "bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 dark:from-purple-900 dark:to-purple-800 dark:text-purple-200"
                          : user?.type === "Trial"
                            ? "bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 dark:from-blue-900 dark:to-blue-800 dark:text-blue-200"
                            : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {user?.type === "Reseller" ? (
                      <i className="fas fa-user-tag mr-1" />
                    ) : user?.type === "Premium" ? (
                      <i className="fas fa-crown mr-1" />
                    ) : user?.type === "Trial" ? (
                      <i className="fas fa-clock mr-1" />
                    ) : (
                      <i className="fas fa-user mr-1" />
                    )}
                    {user?.type || "Free User"}
                  </span>
                )}
                {user?.role !== "admin" && user?.resellerManaged && (
                  <ResellerManagedBadge />
                )}
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200 space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Member Since</span>
                <span className="font-medium">
                  {user?.joinDate
                    ? new Date(user.joinDate).toLocaleDateString("en-US", {
                        month: "short",
                        year: "numeric",
                      })
                    : "Jan 2024"}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Last Login</span>
                <span className="font-medium">Today</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Status</span>
                <span className="badge badge-success">Active</span>
              </div>
              {profileData.company && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Company</span>
                  <span className="font-medium">{profileData.company}</span>
                </div>
              )}
              {profileData.location && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Location</span>
                  <span className="font-medium">{profileData.location}</span>
                </div>
              )}
            </div>

            {user?.role === "user" &&
              !(
                user?.type === "Reseller" && user?.resellerType === "module"
              ) && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <button
                    className="w-full btn btn-outline justify-center"
                    onClick={() => {
                      const email = user?.email || "";

                      window.open(
                        getCustomerPortal(encodeURIComponent(email)),
                        "_blank",
                        "noopener,noreferrer"
                      );
                    }}
                  >
                    <i className="fas fa-credit-card" />
                    Manage Subscription
                  </button>
                </div>
              )}
          </div>
        </motion.div>

        {/* Main Content */}
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-3"
          initial={{ opacity: 0, y: 20 }}
        >
          <div className="card shadow-md">
            {/* Tabs */}
            <div className="border-b border-gray-200">
              <nav aria-label="Tabs" className="flex space-x-8 px-6">
                {tabs.map((tab) => (
                  <button
                    className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors ${
                      activeTab === tab.id
                        ? "border-primary-500 text-primary-600"
                        : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600"
                    }`}
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <i className={`${tab.icon} mr-2`} />
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === "general" && (
                <form className="space-y-6" onSubmit={handleProfileSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="label">First Name</label>
                      <div className="relative">
                        <input
                          className="input pl-10"
                          name="firstName"
                          placeholder="John"
                          type="text"
                          value={profileData.firstName}
                          onChange={handleProfileChange}
                        />
                        <i className="fas fa-user absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      </div>
                    </div>

                    <div>
                      <label className="label">Last Name</label>
                      <div className="relative">
                        <input
                          className="input pl-10"
                          name="lastName"
                          placeholder="Doe"
                          type="text"
                          value={profileData.lastName}
                          onChange={handleProfileChange}
                        />
                        <i className="fas fa-user absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      </div>
                    </div>

                    <div>
                      <label className="label">Email Address</label>
                      <div className="relative">
                        <input
                          className="input pl-10"
                          name="email"
                          placeholder="you@example.com"
                          type="email"
                          value={profileData.email}
                          onChange={handleProfileChange}
                        />
                        <i className="fas fa-envelope absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      </div>
                    </div>

                    <div>
                      <label className="label">Phone Number</label>
                      <div className="relative">
                        <input
                          className="input pl-10"
                          name="phone"
                          placeholder="+1 (555) 123-4567"
                          type="tel"
                          value={profileData.phone}
                          onChange={handleProfileChange}
                        />
                        <i className="fas fa-phone absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      </div>
                    </div>

                    <div>
                      <label className="label">Company</label>
                      <div className="relative">
                        <input
                          className="input pl-10"
                          name="company"
                          placeholder="Acme Inc."
                          type="text"
                          value={profileData.company}
                          onChange={handleProfileChange}
                        />
                        <i className="fas fa-building absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      </div>
                    </div>

                    <div>
                      <label className="label">Position</label>
                      <div className="relative">
                        <input
                          className="input pl-10"
                          name="position"
                          placeholder="Software Developer"
                          type="text"
                          value={profileData.position}
                          onChange={handleProfileChange}
                        />
                        <i className="fas fa-briefcase absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      </div>
                    </div>

                    <div>
                      <label className="label">Location</label>
                      <div className="relative">
                        <input
                          className="input pl-10"
                          name="location"
                          placeholder="New York, USA"
                          type="text"
                          value={profileData.location}
                          onChange={handleProfileChange}
                        />
                        <i className="fas fa-map-marker-alt absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      </div>
                    </div>

                    <div>
                      <label className="label">Website</label>
                      <div className="relative">
                        <input
                          className="input pl-10"
                          name="website"
                          placeholder="https://example.com"
                          type="url"
                          value={profileData.website}
                          onChange={handleProfileChange}
                        />
                        <i className="fas fa-globe absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      </div>
                    </div>

                    <div>
                      <label className="label">Timezone</label>
                      <div className="relative">
                        <select
                          className="input pl-10"
                          name="timezone"
                          value={profileData.timezone}
                          onChange={handleProfileChange}
                        >
                          <option value="UTC">UTC</option>
                          <option value="EST">Eastern Time</option>
                          <option value="CST">Central Time</option>
                          <option value="MST">Mountain Time</option>
                          <option value="PST">Pacific Time</option>
                        </select>
                        <i className="fas fa-clock absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      </div>
                    </div>

                    <div>
                      <label className="label">Bio</label>
                      <textarea
                        className="input"
                        name="bio"
                        placeholder="Tell us about yourself..."
                        rows="1"
                        value={profileData.bio}
                        onChange={handleProfileChange}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-3">
                    <button
                      className="btn btn-primary"
                      disabled={loading}
                      type="submit"
                    >
                      {loading ? (
                        <>
                          <i className="fas fa-spinner fa-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save" />
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}

              {activeTab === "security" && (
                <div className="space-y-6">
                  <div className="card p-6 bg-gray-50 dark:bg-gray-700/50 shadow-md">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Change Password
                    </h3>
                    <form className="space-y-4" onSubmit={handlePasswordSubmit}>
                      <div>
                        <label className="label">Current Password</label>
                        <div className="relative">
                          <input
                            className="input pl-10"
                            name="currentPassword"
                            placeholder="••••••••"
                            type="password"
                            value={passwordData.currentPassword}
                            onChange={handlePasswordChange}
                          />
                          <i className="fas fa-lock absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        </div>
                      </div>

                      <div>
                        <label className="label">New Password</label>
                        <div className="relative">
                          <input
                            className="input pl-10"
                            name="newPassword"
                            placeholder="••••••••"
                            type="password"
                            value={passwordData.newPassword}
                            onChange={handlePasswordChange}
                          />
                          <i className="fas fa-key absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        </div>
                      </div>

                      <div>
                        <label className="label">Confirm New Password</label>
                        <div className="relative">
                          <input
                            className="input pl-10"
                            name="confirmPassword"
                            placeholder="••••••••"
                            type="password"
                            value={passwordData.confirmPassword}
                            onChange={handlePasswordChange}
                          />
                          <i className="fas fa-check-circle absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        </div>
                      </div>

                      <button
                        className="btn btn-primary"
                        disabled={loading}
                        type="submit"
                      >
                        <i className="fas fa-shield-alt" />
                        Update Password
                      </button>
                    </form>
                  </div>
                </div>
              )}

              {activeTab === "preferences" && (
                <div className="space-y-6">
                  <div className="card p-6 bg-gray-50 dark:bg-gray-700/50 shadow-md">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Display Preferences
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg">
                        <div className="flex-1">
                          <label className="font-medium text-gray-900 dark:text-white">
                            Welcome Banner
                          </label>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            Show the welcome banner with helpful tips and
                            premium features on the dashboard
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            checked={preferences.showWelcomeBanner}
                            className="sr-only peer"
                            type="checkbox"
                            onChange={(e) => {
                              const newPreferences = {
                                ...preferences,
                                showWelcomeBanner: e.target.checked,
                              };

                              setPreferences(newPreferences);
                              // Save to API
                              handlePreferencesUpdate(newPreferences);
                            }}
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600" />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "activity" && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Recent Activity
                  </h3>
                  <div className="space-y-4">
                    {[
                      {
                        action: "Logged in",
                        time: "2 hours ago",
                        icon: "fa-sign-in-alt",
                        color: "text-green-600",
                      },
                      {
                        action: "Updated profile",
                        time: "1 day ago",
                        icon: "fa-user-edit",
                        color: "text-blue-600",
                      },
                      {
                        action: "Created ticket #LUN-001234",
                        time: "2 days ago",
                        icon: "fa-ticket-alt",
                        color: "text-purple-600",
                      },
                      {
                        action: "Paid invoice #LUN-2025-00001",
                        time: "3 days ago",
                        icon: "fa-check-circle",
                        color: "text-green-600",
                      },
                      {
                        action: "Changed password",
                        time: "1 week ago",
                        icon: "fa-key",
                        color: "text-yellow-600",
                      },
                    ].map((activity, index) => (
                      <div
                        className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                        key={index}
                      >
                        <div
                          className={`w-10 h-10 rounded-full bg-white dark:bg-gray-700 flex items-center justify-center ${activity.color}`}
                        >
                          <i className={`fas ${activity.icon}`} />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 dark:text-white">
                            {activity.action}
                          </p>
                          <p className="text-sm text-gray-500">
                            {activity.time}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Profile;
